import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {usePalette} from '#/lib/hooks/usePalette'
import {useGetTimeAgo} from '#/lib/hooks/useTimeAgo'
import {useProfileQuery} from '#/state/queries/profile'
import {NativeDropdown} from '#/view/com/util/forms/NativeDropdown'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {type Room} from '../types'

interface RoomItemProps {
  room: Room
}

export function RoomItem({room}: RoomItemProps) {
  const {_} = useLingui()
  const pal = usePalette('default')
  const navigation = useNavigation<any>()
  const timeAgo = useGetTimeAgo()
  const {data: profile} = useProfileQuery({did: room.createdByDid})

  const handleJoinRoom = () => {
    navigation.navigate('RoomCall', {roomId: room.id})
  }

  const handleReport = () => {
    // TODO: Implement report functionality
    console.log('Report room:', room.id)
  }

  const dropdownItems = [
    {
      testID: 'roomReportBtn',
      label: _(msg`Report`),
      onPress: handleReport,
    },
  ]

  const isRoomEmpty = room.participantCount === 0
  const tags = room.tags ? [...room.tags] : []
  if (isRoomEmpty) {
    tags.unshift('Closed')
  }

  const containerStyle = [
    styles.container,
    pal.border,
    isRoomEmpty && styles.endedRoom,
  ]

  const MainContent = isRoomEmpty ? View : TouchableOpacity
  const mainContentProps = isRoomEmpty
    ? {testID: `roomItem-${room.id}-ended`}
    : {
        accessibilityRole: 'button' as const,
        onPress: handleJoinRoom,
        testID: `roomItem-${room.id}`,
      }

  return (
    <MainContent style={containerStyle} {...mainContentProps}>
      {/* Header row with smaller avatar, username, time, and three dots */}
      <View style={styles.headerRow}>
        <UserAvatar type="user" size={28} avatar={profile?.avatar} />

        <View style={styles.headerContent}>
          <View style={styles.userInfo}>
            <Text style={[styles.username, pal.text]} numberOfLines={1}>
              @{room.createdBy}
            </Text>
            <Text style={[styles.timeAgo, pal.textLight]}>
              · {room.createdAt ? timeAgo(new Date(room.createdAt), Date.now()) : ''}
            </Text>
          </View>
        </View>

        <NativeDropdown
          testID={`roomDropdownBtn-${room.id}`}
          items={dropdownItems}
          accessibilityLabel={_(msg`Room options`)}
          accessibilityHint={_(msg`Opens room options menu`)}
        />
      </View>

      {/* Room title/description - left aligned below profile picture */}
      <View style={styles.roomContent}>
        <Text style={[styles.roomTitle, pal.text]} numberOfLines={3}>
          {room.name}
        </Text>

        {/* Tags - positioned below the title */}
        {tags.length > 0 && (
          <View style={styles.tagsContainer}>
            {tags.map((tag, index) => {
              const isRecordingTag = tag === 'Recording'
              const isCamMicTag = tag === 'Cam & mic required'
              const isClosedTag = tag === 'Closed'

              return (
                <View
                  key={index}
                  style={[
                    styles.tag,
                    isRecordingTag && styles.tagRecording,
                    isCamMicTag && styles.tagCamMic,
                    isClosedTag && styles.tagClosed,
                    !isRecordingTag &&
                      !isCamMicTag &&
                      !isClosedTag &&
                      pal.border,
                  ]}>
                  <Text
                    style={[
                      styles.tagText,
                      isRecordingTag && styles.tagTextRecording,
                      isCamMicTag && styles.tagTextCamMic,
                      isClosedTag && styles.tagTextClosed,
                      !isRecordingTag &&
                        !isCamMicTag &&
                        !isClosedTag &&
                        pal.text,
                    ]}>
                    {tag}
                  </Text>
                </View>
              )
            })}
          </View>
        )}

        {/* Room status */}
        <View style={styles.statusContainer}>
          {!isRoomEmpty && (
            <Text style={[styles.statusText, pal.textLight]}>
              <Trans>{room.participantCount} participants</Trans>
            </Text>
          )}
        </View>
      </View>
    </MainContent>
  )
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    padding: 16,
    borderBottomWidth: 1,
  },
  endedRoom: {
    opacity: 0.5,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  headerContent: {
    flex: 1,
    marginLeft: 12,
    marginRight: 12,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  username: {
    fontSize: 15,
    fontWeight: '600',
    marginRight: 4,
  },
  timeAgo: {
    fontSize: 14,
  },
  roomContent: {
    paddingLeft: 0, // Align title below profile picture
  },
  roomTitle: {
    fontSize: 22,
    fontWeight: '500',
    lineHeight: 28,
    marginBottom: 8,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
    gap: 6,
  },
  tag: {
    borderRadius: 16,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  tagRecording: {
    backgroundColor: '#FF3B30',
    borderWidth: 0,
  },
  tagCamMic: {
    backgroundColor: '#FF6B47',
    borderWidth: 0,
  },
  tagClosed: {
    backgroundColor: '#999',
    borderWidth: 0,
  },
  tagText: {
    fontSize: 13,
    fontWeight: '500',
  },
  tagTextRecording: {
    color: '#FFF',
  },
  tagTextCamMic: {
    color: '#FFF',
  },
  tagTextClosed: {
    color: '#FFF',
  },
  statusContainer: {
    marginTop: 4,
  },
  statusText: {
    fontSize: 14,
  },
})
